import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { haptics } from '@/lib/haptics';

interface MobileFullscreenPageProps {
  /** Controls visibility. When false, nothing renders. */
  open: boolean;
  /** Called when the user taps the back chevron, presses Esc, or fires browser back. */
  onClose: () => void;
  /** Page title shown centered (or left-aligned) in the header. */
  title?: ReactNode;
  /** Optional right-side header slot, e.g. a Save pill or kebab menu. */
  trailing?: ReactNode;
  /** Optional sticky bottom action bar. Keyboard-aware. */
  footer?: ReactNode;
  /** Page body. Owns its own scroll — do NOT wrap in another scroll container. */
  children: ReactNode;
  /**
   * Desktop fallback. Until call sites are migrated one-by-one, callers can
   * opt into desktop rendering or short-circuit it (default) and keep using
   * their existing desktop surface.
   */
  desktopFallback?: ReactNode;
  className?: string;
  /** Override the back chevron aria-label. */
  backLabel?: string;
  /**
   * When true (default), the body container is `overflow-y-auto` and owns the
   * page scroll. Set to `false` for callers that manage their own internal
   * scroll + sticky footer (e.g. complex forms). Body becomes `flex-1 min-h-0
   * flex flex-col` so children fill the available height.
   */
  bodyScroll?: boolean;
	/**
	 * Surface treatment. Default `glass` (`.glass-obsidian`) so the underlying
	 * page reads through, which is the right feel for most mobile modals and
	 * sub-pages. Use `solid` for rare top-level flows that need to feel like a
	 * brand-new screen (e.g. a standalone wizard with no visible parent).
	 */
	surface?: 'solid' | 'glass';
}

/**
 * Canonical mobile "drawer that should be a page" shell.
 *
 * Use this instead of a `<Sheet side="bottom">` whenever the surface is a
 * multi-section form, long scrolling content, or a sub-page. Bottom sheets
 * are reserved for messaging composers, short pickers (≤1 screen), and
 * confirm dialogs.
 *
 * Anatomy: 48px header (back chevron + title + optional trailing) → body
 * (owns scroll) → optional sticky footer (keyboard-aware).
 *
 * Default surface is `glass-obsidian` so the parent page reads through behind
 * dimmed glass. Top-level standalone pages can opt into `bg-background` via
 * `surface="solid"`. Respects safe-area top and bottom. Wires popstate so the
 * OS / browser back gesture pops the page instead of leaving the route.
 */
export function MobileFullscreenPage({
  open,
  onClose,
  title,
  trailing,
  footer,
  children,
  desktopFallback = null,
  className,
  backLabel = 'Back',
  bodyScroll = true,
  surface = 'glass',
}: MobileFullscreenPageProps) {
  const isMobile = useIsMobile();
  const { keyboardHeight } = useVisualViewportInset();

  // Keep latest onClose in a ref so the popstate/Esc effects don't re-bind
  // (and trigger history.back) every time a parent re-render hands us a new
  // function identity.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Esc to close
  useEffect(() => {
    if (!open || !isMobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isMobile]);

  // Browser/OS back → close instead of leaving the route.
  // Defer the pushState by one animation frame so React StrictMode's
  // double-invoke of this effect (setup → cleanup → setup on first mount)
  // doesn't push+pop synchronously — which fires a popstate that closes us
  // the instant we open.
  useEffect(() => {
    if (!open || !isMobile) return;
    let installed = false;
    const onPop = () => onCloseRef.current();
    const rafId = requestAnimationFrame(() => {
      installed = true;
      window.history.pushState({ __mobileFullscreen: true }, '');
      window.addEventListener('popstate', onPop);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (!installed) return;
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.__mobileFullscreen) {
        window.history.back();
      }
    };
  }, [open, isMobile]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  if (!open) return null;
  if (!isMobile) return <>{desktopFallback}</>;



  const handleBack = () => {
    haptics.tap();
    onClose();
  };

  const isGlass = surface === 'glass';

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col h-screen-safe',
        isGlass ? 'glass-obsidian' : 'bg-background',
        className,
      )}
      role="dialog"
      aria-modal="true"
    >
      {/* Header */}
      <header
        className="relative z-10 flex h-12 flex-shrink-0 items-center gap-2 border-b border-white/[0.06] px-2"
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3rem + env(safe-area-inset-top))' }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label={backLabel}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground hover:bg-white/[0.06] active:bg-white/[0.1] transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0 truncate text-base font-semibold text-foreground">
          {title}
        </div>
        {trailing ? <div className="flex items-center gap-1">{trailing}</div> : null}
      </header>

      {/* Body — owns the scroll by default; callers can opt out via bodyScroll={false} */}
      <div
        className={cn(
          'relative z-10 flex-1 min-h-0',
          bodyScroll ? 'overflow-y-auto overscroll-contain touch-pan-y' : 'flex flex-col',
        )}
        style={bodyScroll ? { WebkitOverflowScrolling: 'touch' } : undefined}
      >
        {children}
      </div>

      {/* Optional sticky footer */}
      {footer ? (
        <div
          className={cn(
            'relative z-10 flex-shrink-0 border-t border-white/[0.06] px-4 py-3',
            isGlass ? 'glass-obsidian-flat' : 'bg-background',
          )}
          style={{
            paddingBottom: keyboardHeight
              ? `calc(${keyboardHeight}px + 0.75rem)`
              : 'calc(env(safe-area-inset-bottom) + 0.75rem)',
          }}
        >
          {footer}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
